import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useNavigationType, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { ProjectProvider } from '../contexts/ProjectContext';
import { ErrorBoundary } from '../utilities/bugsnag';
import { CircularProgress, Box } from '@mui/material';
import ProtectedRoute from '../components/ProtectedRoute';
import AdminOnlyRoute from '../components/AdminOnlyRoute';
import SuperAdminOnlyRoute from '../components/SuperAdminOnlyRoute';
import PlatformOrgAdminRoute from '../components/PlatformOrgAdminRoute';
import { useAppTheme } from '../hooks/useAppTheme';
import {
  isCelebrationsDomain,
  isMemoriesDomain,
  isOccasionDomain,
} from '../utilities/occasionPortal';
import { isJobsDomain } from '../utilities/jobsPortal';

// Lazy load all components
const LoginScreen = React.lazy(() => import('../pages/LoginScreen'));
const PinListView = React.lazy(() => import('../pages/PinListView'));
const TagsView = React.lazy(() => import('../pages/TagsView'));
const MapView = React.lazy(() => import('../pages/MapView'));
const SplitView = React.lazy(() => import('../pages/SplitView'));
const WanderView = React.lazy(() => import('../pages/WanderView'));
const RecordingFormConfigurator = React.lazy(() => import('../pages/RecordingFormConfigurator'));
const InviteLanding = React.lazy(() => import('../pages/InviteLanding'));
const TestInviteLanding = React.lazy(() => import('../pages/testInviteLanding'));
const AuthorDashboard = React.lazy(() => import('../pages/AuthorDashboard'));
const ProjectView = React.lazy(() => import('../pages/ProjectView'));
const AdminMessageView = React.lazy(() => import('../pages/AdminMessageView'));
const AuthorClaimProcess = React.lazy(() => import('../pages/AuthorClaimProcess'));
const AuthorLoginScreen = React.lazy(() => import('../pages/AuthorLoginScreen'));
const LoginHub = React.lazy(() => import('../auth/components/LoginHub'));
const UserAccessView = React.lazy(() => import('../pages/UserAccessView'));
const BranchView = React.lazy(() => import('../pages/BranchView'));
const OrgRegistrationScreen = React.lazy(() => import('../pages/OrgRegistrationScreen'));
const OrgDashboardScreen = React.lazy(() => import('../pages/OrgDashboardScreen'));
const JoinOrgScreen = React.lazy(() => import('../pages/JoinOrgScreen'));
const ModeratorDashboard = React.lazy(() => import('../pages/ModeratorDashboard'));
const FormsView = React.lazy(() => import('../pages/FormsView'));
const ModerationView = React.lazy(() => import('../pages/ModerationView'));
const ArtistsView = React.lazy(() => import('../pages/ArtistsView'));
const OrphanedFilesView = React.lazy(() => import('../pages/OrphanedFilesView'));
const PlayTestAccountsView = React.lazy(() => import('../pages/PlayTestAccountsView'));
const StatsView = React.lazy(() => import('../pages/StatsView'));
const CompetitionsView = React.lazy(() => import('../pages/CompetitionsView'));
const AppSettingsView = React.lazy(() => import('../pages/AppSettingsView'));
const FacilitatorDashboard = React.lazy(() => import('../pages/FacilitatorDashboard'));
const FacilitatorDatePollPage = React.lazy(() => import('../pages/FacilitatorDatePollPage'));
const WorkshopManagerDashboard = React.lazy(() => import('../pages/WorkshopManagerDashboard'));
const ParticipantDashboard = React.lazy(() => import('../pages/ParticipantDashboard'));
const ParticipantLoginScreen = React.lazy(() => import('../pages/ParticipantLoginScreen'));
const FacilitatorLoginScreen = React.lazy(() => import('../pages/FacilitatorLoginScreen'));
const OrganisationLoginScreen = React.lazy(() => import('../pages/OrganisationLoginScreen'));
const WorkshopLanding = React.lazy(() => import('../pages/WorkshopLanding'));
const WorkshopDirectory = React.lazy(() => import('../pages/WorkshopDirectory'));
const ProjectBudgetView = React.lazy(() => import('../pages/ProjectBudgetView'));
const BudgetTrackerView = React.lazy(() => import('../pages/BudgetTrackerView'));
const AnnualCompanyCostsPage = React.lazy(
  () => import('../pages/finance/AnnualCompanyCostsPage')
);
const NewsletterView = React.lazy(() => import('../pages/NewsletterView'));
const SubscriberView = React.lazy(() => import('../pages/SubscriberView'));
const DripCampaignView = React.lazy(() => import('../pages/DripCampaignView'));
const CommunicationsManagementView = React.lazy(() => import('../pages/CommunicationsManagementView'));
const EmailTemplatesView = React.lazy(() => import('../pages/EmailTemplatesView'));
const WorkshopBookingPage = React.lazy(() => import('../pages/WorkshopBookingPage'));
const OrgSettingsPage = React.lazy(() => import('../pages/OrgSettingsPage'));
const PartnershipDashboard = React.lazy(() => import('../pages/PartnershipDashboard'));
const LeadPipeline = React.lazy(() => import('../pages/LeadPipeline'));
const CRMAnalyticsDashboard = React.lazy(() => import('../pages/CRMAnalyticsDashboard'));
const PrivacyPolicy = React.lazy(() => import('../pages/PrivacyPolicy'));
const UnsubscribePage = React.lazy(() => import('../pages/UnsubscribePage'));
const FeedbackFormBuilderView = React.lazy(() => import('../pages/FeedbackFormBuilderView'));
const FeedbackFormView = React.lazy(() => import('../pages/FeedbackFormView'));
const WanderMakerView = React.lazy(() => import('../pages/WanderMakerView'));
const WanderWriterLoginScreen = React.lazy(() => import('../pages/WanderWriterLoginScreen'));
const WanderWriterDashboard = React.lazy(() => import('../pages/WanderWriterDashboard'));
const PostboxPortal = React.lazy(() => import('../pages/PostboxPortal'));
const PostboxTestHelper = React.lazy(() => import('../pages/PostboxTestHelper'));
const PostboxLanding = React.lazy(() => import('../pages/PostboxLanding'));
const OccasionLanding = React.lazy(() => import('../pages/occasion-portal/OccasionLanding'));
const OccasionCreateWizard = React.lazy(() => import('../pages/occasion-portal/OccasionCreateWizard'));
const OccasionCheckoutSuccessPage = React.lazy(
  () => import('../pages/occasion-portal/OccasionCheckoutSuccessPage')
);
const OccasionManagePage = React.lazy(() => import('../pages/occasion-portal/OccasionManagePage'));
const OccasionMyMapsPage = React.lazy(() => import('../pages/occasion-portal/OccasionMyMapsPage'));
const OccasionContributePage = React.lazy(() => import('../pages/occasion-portal/OccasionContributePage'));
const OccasionCollectPage = React.lazy(() => import('../pages/occasion-portal/OccasionCollectPage'));
const WorkshopMapsAdminPage = React.lazy(() => import('../pages/workshop-maps/WorkshopMapsAdminPage'));
const WorkshopMapSessionPage = React.lazy(() => import('../pages/workshop-maps/WorkshopMapSessionPage'));
const WorkshopMapJoinPage = React.lazy(() => import('../pages/workshop-maps/WorkshopMapJoinPage'));
const WorkshopMapQrLandingPage = React.lazy(() => import('../pages/workshop-maps/WorkshopMapQrLandingPage'));
const WorkStreamBoard = React.lazy(() => import('../pages/WorkStreamBoard'));
const WorkstreamTimelineListPage = React.lazy(
  () => import('../pages/workstream-timeline/WorkstreamTimelineListPage')
);
const WorkstreamTimelinePage = React.lazy(
  () => import('../pages/workstream-timeline/WorkstreamTimelinePage')
);
const WorkstreamTimelinePublicPage = React.lazy(
  () => import('../pages/workstream-timeline/WorkstreamTimelinePublicPage')
);
const OrgFilesView = React.lazy(() => import('../pages/OrgFilesView'));
const SuperAdminDashboard = React.lazy(() => import('../pages/SuperAdminDashboard'));
const EvaluationView = React.lazy(() => import('../pages/EvaluationView'));
const BidsAndCommissionsDashboard = React.lazy(
  () => import('../pages/BidsAndCommissionsDashboard')
);
const FirestoreCostForecastDashboard = React.lazy(
  () => import('../pages/FirestoreCostForecastDashboard')
);
const SocialMediaDeploymentView = React.lazy(
  () => import('../pages/SocialMediaDeploymentView')
);
const TimeTrackingView = React.lazy(() => import('../pages/TimeTrackingView'));
const MeetingsView = React.lazy(() => import('../pages/MeetingsView'));
const TeamUpdatesPage = React.lazy(() => import('../pages/team-updates/TeamUpdatesPage'));
const FacilitatorContractSignPage = React.lazy(
  () => import('../pages/facilitator-contracts/FacilitatorContractSignPage')
);
const JobsLanding = React.lazy(() => import('../pages/jobs/JobsLanding'));
const JobDetailPage = React.lazy(() => import('../pages/jobs/JobDetailPage'));
const JobApplyPage = React.lazy(() => import('../pages/jobs/JobApplyPage'));
const JobReviewPage = React.lazy(() => import('../pages/jobs/JobReviewPage'));
const JobApplicationsAdminPage = React.lazy(
  () => import('../pages/jobs/JobApplicationsAdminPage')
);
const ConsentFormPage = React.lazy(() => import('../pages/consent/ConsentFormPage'));
const ProjectPresentationPage = React.lazy(
  () => import('../pages/project-presentation/ProjectPresentationPage')
);
const WorkExperienceWorksheetPage = React.lazy(
  () => import('../pages/work-experience/WorkExperienceWorksheetPage')
);
const WorkExperienceLeadNotesPage = React.lazy(
  () => import('../pages/work-experience/WorkExperienceLeadNotesPage')
);
const ConsentFormsAdminPage = React.lazy(
  () => import('../pages/consent/ConsentFormsAdminPage')
);

// Loading component
const PageLoader = () => {
  const { theme: appTheme } = useAppTheme();
  
  return (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="50vh"
      sx={{
        backgroundColor: appTheme.colors.background,
        color: appTheme.colors.text,
        transition: 'background-color 0.3s, color 0.3s',
        marginTop: '21px'
      }}
  >
      <CircularProgress 
        sx={{ 
          color: appTheme.colors.primary,
          transition: 'color 0.3s ease'
        }} 
      />
  </Box>
);
};

const AppRoutes: React.FC = () => {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;
  
  // Detect if we're on the postbox domain
  const isPostboxDomain = typeof window !== 'undefined' && (
    window.location.hostname === 'postbox.theoverhear.app' ||
    window.location.hostname === 'postbox-theoverhear.web.app' ||
    window.location.hostname.includes('postbox')
  );

  const isMemoriesHost = typeof window !== 'undefined' && isMemoriesDomain();
  const isCelebrationsHost = typeof window !== 'undefined' && isCelebrationsDomain();
  const isOccasionHost = typeof window !== 'undefined' && isOccasionDomain();
  const isJobsHost = typeof window !== 'undefined' && isJobsDomain();

  const handleConfigChange = (newConfig: Record<string, string | boolean>) => {

  };

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  useEffect(() => {
    let title = "";
    let metaDescription = "";

    // Check for postbox portal routes (paths starting with /pb_)
    if (pathname.startsWith('/pb_')) {
      title = "OVERHEAR Postbox";
      metaDescription = "Send a message to an OVERHEAR Postbox location";
    } else if (pathname === '/postbox') {
      title = "OVERHEAR Postbox";
      metaDescription = "Learn about OVERHEAR Postbox - Send messages to special locations";
    } else if (
      pathname.startsWith('/memories') ||
      pathname.startsWith('/celebrations') ||
      pathname.startsWith('/create') ||
      pathname.startsWith('/map/') ||
      pathname.startsWith('/maps') ||
      pathname.endsWith('/maps') ||
      pathname.startsWith('/contribute/') ||
      pathname.startsWith('/collect/') ||
      isOccasionHost
    ) {
      title = isMemoriesHost || pathname.startsWith('/memories')
        ? 'OVERHEAR Memories'
        : isCelebrationsHost || pathname.startsWith('/celebrations')
          ? 'OVERHEAR Celebrations'
          : 'OVERHEAR';
      metaDescription = 'Create a private geofenced map for celebrations or memorials';
    } else if (pathname.startsWith('/facilitator-contract')) {
      title = 'Sign agreement';
      metaDescription = 'Read and sign your Overhear facilitator agreement';
    } else if (pathname.startsWith('/jobs') || isJobsHost) {
      title = 'Work with OVERHEAR';
      metaDescription = 'Call-outs, freelance roles and other ways to work with Overhear';
    } else if (pathname === '/consent' || pathname.startsWith('/consent/')) {
      title = 'Consent form';
      metaDescription = 'How OVERHEAR may use your story, voice and image';
    } else if (pathname.startsWith('/workstream-timelines')) {
      title = 'Project Workstream Timelines';
      metaDescription = 'Shareable workstream strand timelines';
    } else {
      switch (pathname) {
      case "/":
        title = "";
        metaDescription = "";
        break;
      case "/loginscreen":
        title = "";
        metaDescription = "";
        break;
      case "/pin-list-view":
        title = "";
        metaDescription = "";
        break;
      case "/map-view":
        title = "";
        metaDescription = "";
        break;
      case "/tags-view":
        title = "";
        metaDescription = "";
        break;
      case "/project-view":
        title = "";
        metaDescription = "";
        break;
      case "/message-view":
        title = "";
        metaDescription = "";
        break;
      case "/wanders-view":
        title = "";
        metaDescription = "";
        break;
      case "/wander-maker":
        title = "Wander Maker Studio";
        metaDescription = "Create and structure audio experiences for Wanders";
        break;
      case "/user-access-view":
        title = "";
        metaDescription = "";
        break;
      case "/form-builder-view":
        title = "";
        metaDescription = "";
        break;
      case "/forms-view":
        title = "";
        metaDescription = "";
        break;
      case "/author-dashboard":
        title = "";
        metaDescription = "";
        break;
      case "/author-login":
        title = "";
        metaDescription = "";
        break;
      case "/author-claim-process":
        title = "";
        metaDescription = "";
        break;
      case "/invite/:inviteId":
        title = "Invite";
        metaDescription = "You've been invited to a project.";
        break;
      case "/test-invite":
        title = "Test Invite";
        metaDescription = "A test invite link.";
        break;
      case "/branch-view":
        title = "";
        metaDescription = "";
        break;
      case "/register-org":
        title = "Register Organisation";
        metaDescription = "Register your organisation";
        break;
      case "/org-dashboard":
        title = "Organisation Dashboard";
        metaDescription = "Manage your organisation";
        break;
      case "/join-org/:accessCode":
        title = "Join Organisation";
        metaDescription = "Join an organisation with your access code";
        break;
      case "/moderator-dashboard":
        title = "Moderator Dashboard";
        metaDescription = "Manage your organisation's members and groups";
        break;
      case "/moderation-view":
        title = "Content Moderation";
        metaDescription = "Review and moderate content that requires attention";
        break;
      case "/artists-view":
        title = "Artist Profiles Management";
        metaDescription = "Manage and merge artist profiles";
        break;
      case "/orphaned-files":
        title = "Orphaned Files";
        metaDescription = "Manage orphaned author images";
        break;
      case "/play-test-accounts":
        title = "Play test accounts";
        metaDescription = "Review and delete Google Play pre-launch test installs";
        break;
      case "/competitions-view":
        title = "Competitions";
        metaDescription = "Manage app-wide competitions";
        break;
      case "/app-settings-view":
        title = "App Settings";
        metaDescription = "Manage Firestore appSettings for the Overhear mobile app";
        break;
      case "/facilitator-dashboard":
        title = "Facilitator Dashboard";
        metaDescription = "Manage your workshop availability";
        break;
      case "/workshop-manager":
        title = "Workshop Manager";
        metaDescription = "SuperAdmin Workshop Management";
        break;
      case "/participant-dashboard":
        title = "Workshop Directory";
        metaDescription = "Discover and register for Heritage Audio/Creative Writing workshops";
        break;
      case "/workshop-landing":
        title = "Workshop Registration";
        metaDescription = "Register your interest in Heritage Audio/Creative Writing workshops";
        break;
      case "/workshop-directory":
        title = "Workshop Directory";
        metaDescription = "Discover and register for Heritage Audio/Creative Writing workshops across all organisations";
        break;
      case "/workshop-booking":
        title = "Book Custom Workshop";
        metaDescription = "Book a custom Heritage Audio/Creative Writing workshop for your organisation";
        break;
      case "/participant-login":
        title = "Workshop Portal Login";
        metaDescription = "Sign in to your workshop portal to manage your interests and availability";
        break;
      case "/project-budget":
        title = "Project Budget Creator";
        metaDescription = "Build proposal budgets and fee rates";
        break;
      case "/budget-tracker":
        title = "Budget Tracker";
        metaDescription = "Track spend against project pot envelopes";
        break;
      case "/annual-company-costs":
        title = "Annual company costs";
        metaDescription = "Manage recurring company costs funded from the company kitty";
        break;
      case "/newsletter-view":
        title = "Newsletter Management";
        metaDescription = "Create and manage email newsletters";
        break;
      case "/subscriber-view":
        title = "Subscriber Management";
        metaDescription = "Manage email subscribers";
        break;
      case "/drip-campaign-view":
        title = "Drip Campaign Management";
        metaDescription = "Create and manage automated email sequences";
        break;
      case "/email-management":
        title = "Communications Management";
        metaDescription = "Manage newsletters, drip campaigns, templates, and subscribers";
        break;
      case "/partnership-dashboard":
        title = "Partnership Dashboard";
        metaDescription = "Manage partnerships and project inquiries";
        break;
      case "/lead-pipeline":
        title = "Lead Pipeline";
        metaDescription = "Track potential partners before they become inquiries";
        break;
      case "/bids-and-commissions":
        title = "Bids & Commissions";
        metaDescription = "Track bids, tenders and commissions across organisations";
        break;
      case "/crm-analytics":
        title = "CRM Analytics Dashboard";
        metaDescription = "Comprehensive analytics for partnerships, inquiries, and revenue forecasting";
        break;
      case "/workstream":
        title = "Work Stream Board";
        metaDescription = "Track work streams, accountability, and paid work hour reservations";
        break;
      case "/workstream-timelines":
        title = "Project Workstream Timelines";
        metaDescription = "Shareable workstream strand timelines";
        break;
      case "/org-files":
        title = "Organisation Files";
        metaDescription = "Organisational knowledge base and file management";
        break;
      case "/org-settings":
        title = "Organisation Settings";
        metaDescription = "Manage your organisation's settings and preferences";
        break;
      case "/social-media-deployment":
        title = "Social Media Deployment";
        metaDescription = "Compose and publish posts to Bluesky and LinkedIn";
        break;
      case "/time-tracking":
        title = "Time Tracking";
        metaDescription = "Track time on work streams and custom tasks";
        break;
      case "/meetings":
        title = "Meetings";
        metaDescription = "Schedule meetings, create agendas, and publish minutes";
        break;
      case "/privacy":
        title = "Privacy Policy";
        metaDescription = "Overhear CRM Privacy Policy - How we collect, use, and protect your information";
        break;
      case "/work-experience":
        title = "OVERHEAR first instincts";
        metaDescription = "A work experience worksheet for a first use of the OVERHEAR app";
        break;
      case "/work-experience/lead":
        title = "OVERHEAR first instincts — session notes";
        metaDescription = "Notes for running the OVERHEAR work experience worksheet";
        break;
      }
    }

    if (title) {
      document.title = title;
    }

    if (metaDescription) {
      const metaDescriptionTag: HTMLMetaElement | null = document.querySelector(
        'head > meta[name="description"]'
      );
      if (metaDescriptionTag) {
        metaDescriptionTag.content = metaDescription;
      }
    }
  }, [pathname, isOccasionHost, isMemoriesHost, isCelebrationsHost, isJobsHost]);

  return (
    <ErrorBoundary>
      <ProjectProvider>
        <Routes>
          {/* Public routes - no authentication required */}
          <Route 
            path="/" 
            element={
              <Suspense fallback={<PageLoader />}>
                {isPostboxDomain ? (
                  <PostboxLanding />
                ) : isMemoriesHost ? (
                  <OccasionLanding occasionType="memorial" />
                ) : isCelebrationsHost ? (
                  <OccasionLanding occasionType="celebration" />
                ) : isJobsHost ? (
                  <JobsLanding />
                ) : (
                  <LoginHub />
                )}
              </Suspense>
            } 
          />
          {/* Occasion portals (memories / celebrations) — explicit paths, not catch-all */}
          <Route path="/memories" element={<Suspense fallback={<PageLoader />}><OccasionLanding occasionType="memorial" /></Suspense>} />
          <Route path="/celebrations" element={<Suspense fallback={<PageLoader />}><OccasionLanding occasionType="celebration" /></Suspense>} />
          <Route
            path="/memories/create"
            element={<Suspense fallback={<PageLoader />}><OccasionCreateWizard occasionType="memorial" /></Suspense>}
          />
          <Route
            path="/celebrations/create"
            element={<Suspense fallback={<PageLoader />}><OccasionCreateWizard occasionType="celebration" /></Suspense>}
          />
          <Route
            path="/memories/create/success"
            element={
              <Suspense fallback={<PageLoader />}>
                <OccasionCheckoutSuccessPage occasionType="memorial" />
              </Suspense>
            }
          />
          <Route
            path="/celebrations/create/success"
            element={
              <Suspense fallback={<PageLoader />}>
                <OccasionCheckoutSuccessPage occasionType="celebration" />
              </Suspense>
            }
          />
          <Route
            path="/memories/maps"
            element={<Suspense fallback={<PageLoader />}><OccasionMyMapsPage occasionType="memorial" /></Suspense>}
          />
          <Route
            path="/celebrations/maps"
            element={<Suspense fallback={<PageLoader />}><OccasionMyMapsPage occasionType="celebration" /></Suspense>}
          />
          <Route
            path="/maps"
            element={<Suspense fallback={<PageLoader />}><OccasionMyMapsPage /></Suspense>}
          />
          <Route
            path="/create/success"
            element={
              <Suspense fallback={<PageLoader />}>
                {isMemoriesHost ? (
                  <OccasionCheckoutSuccessPage occasionType="memorial" />
                ) : isCelebrationsHost ? (
                  <OccasionCheckoutSuccessPage occasionType="celebration" />
                ) : (
                  <OccasionLanding />
                )}
              </Suspense>
            }
          />
          <Route
            path="/create"
            element={
              <Suspense fallback={<PageLoader />}>
                {isMemoriesHost ? (
                  <OccasionCreateWizard occasionType="memorial" />
                ) : isCelebrationsHost ? (
                  <OccasionCreateWizard occasionType="celebration" />
                ) : (
                  <OccasionLanding />
                )}
              </Suspense>
            }
          />
          <Route path="/map/:projectId" element={<Suspense fallback={<PageLoader />}><OccasionManagePage /></Suspense>} />
          <Route path="/contribute/:token" element={<Suspense fallback={<PageLoader />}><OccasionContributePage /></Suspense>} />
          <Route path="/collect/:code" element={<Suspense fallback={<PageLoader />}><OccasionCollectPage /></Suspense>} />
          <Route path="/present/:token" element={<Suspense fallback={<PageLoader />}><ProjectPresentationPage /></Suspense>} />
          <Route
            path="/workshop-maps/join/:inviteId"
            element={<Suspense fallback={<PageLoader />}><WorkshopMapJoinPage /></Suspense>}
          />
          <Route
            path="/workshop-maps/qr/:joinToken"
            element={<Suspense fallback={<PageLoader />}><WorkshopMapQrLandingPage /></Suspense>}
          />
          <Route path="/workshop-maps" element={
            <SuperAdminOnlyRoute allowFacilitator>
              <Suspense fallback={<PageLoader />}><WorkshopMapsAdminPage /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/workshop-maps/sessions/:sessionId" element={
            <SuperAdminOnlyRoute allowFacilitator>
              <Suspense fallback={<PageLoader />}><WorkshopMapSessionPage /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/login" element={<Suspense fallback={<PageLoader />}><LoginHub /></Suspense>} />
          <Route path="/loginscreen" element={<Suspense fallback={<PageLoader />}><LoginScreen /></Suspense>} />
          <Route path="/register-org" element={<Suspense fallback={<PageLoader />}><OrgRegistrationScreen /></Suspense>} />
          <Route path="/join-org/:accessCode" element={<Suspense fallback={<PageLoader />}><JoinOrgScreen /></Suspense>} />
          <Route path="/invite/:inviteId" element={<Suspense fallback={<PageLoader />}><InviteLanding /></Suspense>} />
          <Route path="/test-invite" element={<Suspense fallback={<PageLoader />}><TestInviteLanding /></Suspense>} />
          <Route path="/author-login" element={<Suspense fallback={<PageLoader />}><AuthorLoginScreen /></Suspense>} />
          <Route path="/workshop-landing" element={<Suspense fallback={<PageLoader />}><WorkshopLanding /></Suspense>} />
          <Route path="/workshop-landing/:organisationId" element={<Suspense fallback={<PageLoader />}><WorkshopLanding /></Suspense>} />
          <Route path="/workshop-landing/:organisationId/:workshopId" element={<Suspense fallback={<PageLoader />}><WorkshopLanding /></Suspense>} />
          <Route path="/workshop-directory" element={<Suspense fallback={<PageLoader />}><WorkshopDirectory /></Suspense>} />
          <Route path="/workshop-booking" element={<Suspense fallback={<PageLoader />}><WorkshopBookingPage /></Suspense>} />
          <Route path="/workshop-booking/:organisationId" element={<Suspense fallback={<PageLoader />}><WorkshopBookingPage /></Suspense>} />
          <Route path="/participant-login" element={<Suspense fallback={<PageLoader />}><ParticipantLoginScreen /></Suspense>} />
          <Route path="/facilitator-login" element={<Suspense fallback={<PageLoader />}><FacilitatorLoginScreen /></Suspense>} />
          <Route path="/organisation-login" element={<Suspense fallback={<PageLoader />}><OrganisationLoginScreen /></Suspense>} />
          <Route path="/wander-writer-login" element={<Suspense fallback={<PageLoader />}><WanderWriterLoginScreen /></Suspense>} />
          <Route path="/project-questionnaire" element={<Suspense fallback={<PageLoader />}><ProjectBudgetView questionnaire={true} /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
          <Route path="/work-experience/lead" element={<Suspense fallback={<PageLoader />}><WorkExperienceLeadNotesPage /></Suspense>} />
          <Route path="/work-experience" element={<Suspense fallback={<PageLoader />}><WorkExperienceWorksheetPage /></Suspense>} />
          <Route path="/consent" element={<Suspense fallback={<PageLoader />}><ConsentFormPage /></Suspense>} />
          <Route path="/unsubscribe" element={<Suspense fallback={<PageLoader />}><UnsubscribePage /></Suspense>} />
          <Route path="/feedback/:formId" element={<Suspense fallback={<PageLoader />}><FeedbackFormView /></Suspense>} />
          <Route path="/postbox-test-helper" element={<Suspense fallback={<PageLoader />}><PostboxTestHelper /></Suspense>} />
          <Route path="/postbox" element={<Suspense fallback={<PageLoader />}><PostboxLanding /></Suspense>} />
          <Route path="/jobs" element={<Suspense fallback={<PageLoader />}><JobsLanding /></Suspense>} />
          <Route path="/jobs/:slug/apply" element={<Suspense fallback={<PageLoader />}><JobApplyPage /></Suspense>} />
          <Route path="/jobs/:slug/review" element={<Suspense fallback={<PageLoader />}><JobReviewPage /></Suspense>} />
          <Route path="/jobs/:slug" element={<Suspense fallback={<PageLoader />}><JobDetailPage /></Suspense>} />
          <Route path="/facilitator-contract/:contractId" element={<Suspense fallback={<PageLoader />}><FacilitatorContractSignPage /></Suspense>} />
          <Route path="/facilitator-contract" element={<Suspense fallback={<PageLoader />}><FacilitatorContractSignPage /></Suspense>} />
          {isJobsHost ? (
            <>
              <Route path="/:slug/apply" element={<Suspense fallback={<PageLoader />}><JobApplyPage /></Suspense>} />
              <Route path="/:slug/review" element={<Suspense fallback={<PageLoader />}><JobReviewPage /></Suspense>} />
              <Route path="/:slug" element={<Suspense fallback={<PageLoader />}><JobDetailPage /></Suspense>} />
            </>
          ) : (
            <Route path="/:token" element={<Suspense fallback={<PageLoader />}><PostboxPortal /></Suspense>} />
          )}
          
          {/* Protected routes - authentication required */}
          <Route path="/org-dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><OrgDashboardScreen /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/org-settings" element={<Suspense fallback={<PageLoader />}><OrgSettingsPage /></Suspense>} />
          <Route path="/moderator-dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><ModeratorDashboard /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/facilitator-dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><FacilitatorDashboard /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/facilitator-date-poll/:pollId" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><FacilitatorDatePollPage /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/team-updates" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><TeamUpdatesPage /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/team-updates/:groupId" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><TeamUpdatesPage /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/workshop-manager" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><WorkshopManagerDashboard /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/pin-list-view" element={
            <AdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><PinListView /></Suspense>
            </AdminOnlyRoute>
          } />
          <Route path="/map-view" element={
            <AdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><MapView /></Suspense>
            </AdminOnlyRoute>
          } />
          <Route path="/split-view" element={
            <AdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><SplitView /></Suspense>
            </AdminOnlyRoute>
          } />
          <Route path="/tags-view" element={
            <AdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><TagsView /></Suspense>
            </AdminOnlyRoute>
          } />
          <Route path="/evaluation-view" element={
            <AdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><EvaluationView /></Suspense>
            </AdminOnlyRoute>
          } />
          <Route path="/super-admin-dashboard" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><SuperAdminDashboard /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/project-view" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><ProjectView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/message-view" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><AdminMessageView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/wanders-view" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><WanderView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/wander-maker" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><WanderMakerView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/user-access-view" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><UserAccessView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/moderation-view" element={
            <SuperAdminOnlyRoute allowOrgAdmin>
              <Suspense fallback={<PageLoader />}><ModerationView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/artists-view" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><ArtistsView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/orphaned-files" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><OrphanedFilesView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/play-test-accounts" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><PlayTestAccountsView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/stats-view" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><StatsView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/competitions-view" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><CompetitionsView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/app-settings-view" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><AppSettingsView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/form-builder-view" element={
            <AdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><RecordingFormConfigurator onChange={handleConfigChange} /></Suspense>
            </AdminOnlyRoute>
          } />
          <Route path="/forms-view/:project_id" element={
            <Suspense fallback={<PageLoader />}><FormsView /></Suspense>
          } />
          <Route path="/author-dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><AuthorDashboard /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/wander-writer-dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><WanderWriterDashboard /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/author-claim-process" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><AuthorClaimProcess /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/branch-view" element={
            <AdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><BranchView /></Suspense>
            </AdminOnlyRoute>
          } />
          <Route path="/participant-dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><ParticipantDashboard /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/project-budget" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><ProjectBudgetView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/budget-tracker" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><BudgetTrackerView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/annual-company-costs" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><AnnualCompanyCostsPage /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/newsletter-view" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><NewsletterView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/subscriber-view" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><SubscriberView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/drip-campaign-view" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><DripCampaignView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/email-management" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><CommunicationsManagementView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/email-templates" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><EmailTemplatesView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/partnership-dashboard" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><PartnershipDashboard /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/lead-pipeline" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><LeadPipeline /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/crm-analytics" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><CRMAnalyticsDashboard /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/bids-and-commissions" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><BidsAndCommissionsDashboard /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/job-applications" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><JobApplicationsAdminPage /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/consent-forms" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><ConsentFormsAdminPage /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/firestore-cost-forecast" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><FirestoreCostForecastDashboard /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/social-media-deployment" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><SocialMediaDeploymentView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/social-media-deployment/instagram-oauth" element={
            <SuperAdminOnlyRoute>
              <Suspense fallback={<PageLoader />}><SocialMediaDeploymentView /></Suspense>
            </SuperAdminOnlyRoute>
          } />
          <Route path="/time-tracking" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><TimeTrackingView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/living-wage-hours" element={
            <PlatformOrgAdminRoute>
              <Navigate to="/workstream?tab=tasks" replace />
            </PlatformOrgAdminRoute>
          } />
          <Route path="/meetings" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><MeetingsView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/workstream" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><WorkStreamBoard /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          <Route path="/workstream-timelines" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><WorkstreamTimelineListPage /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/workstream-timelines/view/:token" element={
            <Suspense fallback={<PageLoader />}><WorkstreamTimelinePublicPage /></Suspense>
          } />
          <Route path="/workstream-timelines/:timelineId" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><WorkstreamTimelinePage /></Suspense>
            </ProtectedRoute>
          } />
          <Route path="/org-files" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><OrgFilesView /></Suspense>
            </PlatformOrgAdminRoute>
          } />
          
          <Route path="/feedback-forms" element={
            <PlatformOrgAdminRoute>
              <Suspense fallback={<PageLoader />}><FeedbackFormBuilderView /></Suspense>
            </PlatformOrgAdminRoute>
          } />

          {/* Catch-all route for postbox tokens - must be last to avoid matching other routes */}
          <Route path="/:token" element={<Suspense fallback={<PageLoader />}><PostboxPortal /></Suspense>} />
          
          <Route path="*" element={<div>Page not found</div>} />
        </Routes>
      </ProjectProvider>
    </ErrorBoundary>
  );
};

export default AppRoutes; 